import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearPreparation,
  getPreparation,
  startPreparation,
  updatePreparedPiece,
  type Preparation,
  type PreparedPiece,
} from "../api";
import { useT } from "../i18n";
import Disclosure from "./Disclosure";
import MutationError from "./MutationError";

/**
 * addedMarks returns the code-point indexes in `prepared` that are not in
 * `source` — the marks the preparation added. The server has already checked
 * that nothing else differs, so a walk that skips what does not match is exact.
 */
export function addedMarks(source: string, prepared: string): Set<number> {
  const src = Array.from(source.trim());
  const out = Array.from(prepared.trim());
  const added = new Set<number>();
  let i = 0;
  for (let j = 0; j < out.length; j++) {
    if (i < src.length && out[j] === src[i]) {
      i++;
    } else {
      added.add(j);
    }
  }
  return added;
}

/**
 * canPrepare is whether preparing is offered for this chapter at all.
 *
 * It is the server's `available` and nothing else (ADR-0064): the marks a
 * preparation adds are kasre-ye ezafe, the hamza above a silent heh and the
 * Persian comma, so it is offered on a book recorded as Persian and on no
 * other — including a book with no language, which is not Persian either.
 */
export function canPrepare(p?: Preparation): boolean {
  return !!p?.available;
}

/**
 * canPrepareFirst is whether to offer preparing as part of narrating: the
 * capability is available here *and* there is text it has not marked yet.
 *
 * Both halves, in one place, because asking only the second half is the bug
 * this replaced — on an English book every piece is unprepared forever, so a
 * control gated on "there is unprepared text" alone showed a Persian-only,
 * paid call on every English chapter. Every control that can start preparing
 * asks one of these two functions; none of them reads the counts directly.
 */
export function canPrepareFirst(p?: Preparation): boolean {
  return canPrepare(p) && p!.complete < p!.total;
}

/**
 * PreparationPanel prepares a chapter's text for narration (ADR-0033): the
 * language model adds kasre-ye ezafe and commas, a piece at a time, and every
 * answer is checked to have added nothing but those marks.
 *
 * The author's text is never changed — only the narrator hears the marks — and
 * each mark can be taken back from the review, because a wrong kasra is spoken
 * as confidently as a right one.
 */
export default function PreparationPanel({ chapterID }: { chapterID: string }) {
  const t = useT();
  const client = useQueryClient();
  const [reviewing, setReviewing] = useState(false);

  const preparation = useQuery({
    queryKey: ["preparation", chapterID],
    queryFn: () => getPreparation(chapterID),
    retry: false,
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 1500 : false),
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["preparation", chapterID] });
    // Marks are part of what a render is keyed on: the estimate moves with them.
    void client.invalidateQueries({ queryKey: ["estimate", chapterID] });
  };
  const start = useMutation({
    mutationFn: () => startPreparation(chapterID),
    onSuccess: (data) => {
      client.setQueryData(["preparation", chapterID], data);
      refresh();
    },
  });
  const clear = useMutation({
    mutationFn: () => clearPreparation(chapterID),
    onSuccess: () => {
      setReviewing(false);
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: (v: { piece: string; prepared: string }) =>
      updatePreparedPiece(chapterID, v.piece, v.prepared),
    onSuccess: (data) => {
      client.setQueryData(["preparation", chapterID], data);
      refresh();
    },
  });

  // Nothing to offer where the chapter cannot be prepared by this person, or
  // where the book is not in a language preparation is written for — the marks
  // it adds are Persian ones (ADR-0033).
  if (!canPrepare(preparation.data)) return null;
  const p = preparation.data!;
  const started = p.complete + p.pending + p.failed > 0;
  const busy = start.isPending || clear.isPending || update.isPending;

  // The row's one line: where the preparation stands.
  const summary = !started
    ? t.narration.prepareNotStarted
    : p.pending > 0
      ? t.narration.preparing(p.complete + p.failed, p.total)
      : p.failed > 0
        ? t.narration.prepareFailed(p.failed)
        : t.narration.prepareSummary(p.marks);

  return (
    <Disclosure
      testId="preparation-panel"
      toggleTestId="preparation-toggle"
      title={t.narration.prepareTitle}
      summary={summary}
      defaultOpen={p.pending > 0}
      className="mt-3"
    >
      {!started && (
        <>
          <p className="mt-1 text-xs text-muted">{t.narration.prepareNote}</p>
          <button
            type="button"
            data-testid="prepare-text"
            disabled={busy}
            onClick={() => start.mutate()}
            className="mt-2 rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
          >
            {t.narration.prepare}
          </button>
        </>
      )}

      {p.pending > 0 && (
        <p className="mt-1 text-sm text-muted" data-testid="preparation-progress">
          {t.narration.preparing(p.complete + p.failed, p.total)}
        </p>
      )}

      {started && p.pending === 0 && (
        <>
          <p className="mt-1 text-sm text-ink-soft" data-testid="preparation-summary">
            {t.narration.prepared(p.marks, p.complete, p.total)}
          </p>

          {p.pieces.some((piece) => piece.note) && (
            <div data-testid="preparation-notes" className="mt-2 text-sm text-ink-soft">
              <p>{t.narration.prepareNotes}</p>
              <ul className="mt-1 list-disc ps-5 text-xs text-muted">
                {p.pieces
                  .filter((piece) => piece.note)
                  .map((piece) => (
                    <li key={piece.index} dir="auto">
                      {piece.note}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {p.failed > 0 && (
            <div
              role="alert"
              data-testid="preparation-failed"
              className="mt-2 text-sm text-red-600 dark:text-red-400"
            >
              <p>{t.narration.prepareFailed(p.failed)}</p>
              <ul className="mt-1 list-disc ps-5 text-xs">
                {p.pieces
                  .filter((piece) => piece.status === "failed")
                  .map((piece) => (
                    <li key={piece.index} dir="auto">
                      {piece.error}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div className="mt-2 flex flex-wrap gap-2">
            {p.complete + p.failed < p.total || p.failed > 0 ? (
              <button
                type="button"
                data-testid="retry-preparation"
                disabled={busy}
                onClick={() => start.mutate()}
                className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
              >
                {p.failed > 0 ? t.narration.prepareRetry : t.narration.prepareRest}
              </button>
            ) : null}
            {p.marks > 0 && (
              <button
                type="button"
                data-testid="review-marks"
                aria-expanded={reviewing}
                onClick={() => setReviewing((open) => !open)}
                className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
              >
                {reviewing ? t.narration.hideMarks : t.narration.reviewMarks}
              </button>
            )}
            <button
              type="button"
              data-testid="clear-preparation"
              disabled={busy}
              onClick={() => clear.mutate()}
              className="rounded-control px-3 py-1.5 text-sm text-muted transition-colors hover:text-ink disabled:opacity-40"
            >
              {t.narration.clearPreparation}
            </button>
          </div>
        </>
      )}

      {/* All three, not just Start. Clearing and un-marking both look like they
          worked when they did not: the panel repaints from the query, which
          still holds what the server kept. */}
      <MutationError
        className="mt-2"
        testId="preparation-error"
        error={start.error ?? clear.error ?? update.error}
        fallback={t.common.somethingWrongTryAgain}
      />

      {reviewing && (
        <div className="mt-3" data-testid="preparation-review">
          <p className="mb-2 text-xs text-muted">{t.narration.reviewNote}</p>
          <div className="max-h-96 space-y-3 overflow-y-auto">
            {p.pieces
              .filter((piece) => piece.marks > 0 && piece.prepared && piece.id)
              .map((piece) => (
                <MarkedPiece
                  key={piece.index}
                  piece={piece}
                  disabled={busy}
                  onRemove={(prepared) => update.mutate({ piece: piece.id!, prepared })}
                />
              ))}
          </div>
        </div>
      )}
    </Disclosure>
  );
}

/**
 * MarkedPiece shows a prepared piece with each word that gained a mark
 * highlighted. The highlight is on the whole word, not the mark: a span around a
 * lone kasra would break the letters' joining in Persian script.
 */
function MarkedPiece({
  piece,
  disabled,
  onRemove,
}: {
  piece: PreparedPiece;
  disabled: boolean;
  onRemove: (prepared: string) => void;
}) {
  const chars = Array.from((piece.prepared ?? "").trim());
  const added = addedMarks(piece.source, piece.prepared ?? "");

  // Words and the whitespace between them, as code-point ranges.
  const tokens: { start: number; end: number; space: boolean }[] = [];
  for (let k = 0; k < chars.length;) {
    const space = /\s/.test(chars[k]!);
    let end = k;
    while (end < chars.length && /\s/.test(chars[end]!) === space) end++;
    tokens.push({ start: k, end, space });
    k = end;
  }

  return (
    <p dir="auto" className="text-base leading-loose">
      {tokens.map((tok) => {
        const text = chars.slice(tok.start, tok.end).join("");
        const marks = [...added].filter((i) => i >= tok.start && i < tok.end);
        if (tok.space || marks.length === 0) return <span key={tok.start}>{text}</span>;
        return (
          <button
            key={tok.start}
            type="button"
            data-testid="prepared-mark"
            disabled={disabled}
            onClick={() => onRemove(chars.filter((_, i) => !marks.includes(i)).join(""))}
            className="rounded-[0.3rem] bg-sage/20 px-0.5 hover:bg-red-500/20 disabled:opacity-50"
          >
            {text}
          </button>
        );
      })}
    </p>
  );
}
